import React, { useEffect } from 'react';
import styles from './ReportCardChart.module.scss';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LabelList,
} from 'recharts';
import { reportTitle } from '../../../../constants/reportTitle';
import {
  completeShowInCutaway,
  completeShowInCutawayWithClosed,
  periodFact,
  periodShowInCutaway,
  periodShowInCutawayWithClosed,
  periodWithClosed,
} from '../../data/reportChartData';
import { ReportCardChartLegend } from '../ReportCardChartLegend/ReportCardChartLegend';
import DspxPagination from '../../../../../../components/DspxPagination/DspxPagination';
import usePagination from '../../../../../../hooks/usePagination';

export const ReportCardChart = ({
  data,
  cardName,
  count,
  showInCutawayState,
  useTheFactState,
}) => {
  const ITEMS_PER_PAGE = cardName === 'period' ? 2 : 4;

  const isNoData =
    !Array.isArray(data) ||
    data.length === 0 ||
    (cardName !== 'period' && !count);

  const [currentPage, setCurrentPage, maxPage, shownItems] = usePagination(
    data,
    ITEMS_PER_PAGE
  );

  const handlePageChange = (page) => {
    const pageNum = page - 1;

    setCurrentPage(pageNum);
  };

  return (
    <div className={styles.root}>
      <>
        <ResponsiveContainer
          width="100%"
          height={380}
        >
          <BarChart
            data={cardName === 'period' ? shownItems : !count ? [] : shownItems}
            margin={{ top: 40, bottom: 10 }}
            barCategoryGap="4px"
            barGap="4%"
          >
            <CartesianGrid
              vertical={false}
              stroke="#56CCF266"
            />
            <Legend
              content={({ payload }) => (
                <ReportCardChartLegend
                  payload={payload}
                  isPeriod={cardName === 'period'}
                />
              )}
              align="center"
              verticalAlign="bottom"
              wrapperStyle={{ paddingTop: '20px' }}
            />

            <XAxis
              fontSize={11}
              className={styles.xaxis}
              dataKey="regionName"
              axisLine={false}
              tickLine={false}
              tickMargin={12}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              stroke="#00000099"
            />

            {!isNoData && (
              <Tooltip
                wrapperStyle={{ textAlign: 'start' }}
                cursor={false}
              />
            )}

            {cardName === 'period' ? (
              <>
                {showInCutawayState && !useTheFactState ? (
                  <>
                    {periodShowInCutaway.map((item) => (
                      <Bar
                        isAnimationActive={false}
                        key={item.dataKey}
                        dataKey={item.dataKey}
                        barSize={30}
                        radius={8}
                        fill={item.fill}
                        name={item.name}
                      >
                        <LabelList
                          dataKey={item.dataKey}
                          position="top"
                          style={{
                            fontSize: 13,
                            fill: item.fill,
                            fontWeight: 'bold',
                          }}
                        />
                      </Bar>
                    ))}
                  </>
                ) : showInCutawayState && useTheFactState ? (
                  <>
                    {periodShowInCutawayWithClosed.map((item) => (
                      <Bar
                        isAnimationActive={false}
                        key={item.dataKey}
                        dataKey={item.dataKey}
                        barSize={30}
                        radius={8}
                        fill={item.fill}
                        name={item.name}
                      >
                        <LabelList
                          dataKey={item.dataKey}
                          position="top"
                          style={{
                            fontSize: 13,
                            fill: item.fill,
                            fontWeight: 'bold',
                          }}
                        />
                      </Bar>
                    ))}
                  </>
                ) : useTheFactState ? (
                  <>
                    {periodWithClosed.map((item) => (
                      <Bar
                        isAnimationActive={false}
                        key={item.dataKey}
                        dataKey={item.dataKey}
                        barSize={30}
                        radius={8}
                        fill={item.fill}
                        name={item.name}
                      >
                        <LabelList
                          dataKey={item.dataKey}
                          position="top"
                          style={{
                            fontSize: 13,
                            fill: item.fill,
                            fontWeight: 'bold',
                          }}
                        />
                      </Bar>
                    ))}
                  </>
                ) : (
                  <>
                    {periodFact.map((item) => (
                      <Bar
                        isAnimationActive={false}
                        key={item.dataKey}
                        dataKey={item.dataKey}
                        barSize={30}
                        radius={8}
                        fill={item.fill}
                        name={item.name}
                      >
                        <LabelList
                          dataKey={item.dataKey}
                          position="top"
                          style={{
                            fontSize: 13,
                            fill: item.fill,
                            fontWeight: 'bold',
                          }}
                        />
                      </Bar>
                    ))}
                  </>
                )}
              </>
            ) : (
              <>
                {showInCutawayState &&
                !useTheFactState &&
                cardName === 'complete' ? (
                  <>
                    {completeShowInCutaway.map((item) => (
                      <Bar
                        isAnimationActive={false}
                        key={item.dataKey}
                        dataKey={item.dataKey}
                        barSize={40}
                        radius={8}
                        fill={item.fill}
                        name={reportTitle[cardName] + ' ' + item.name}
                      >
                        <LabelList
                          dataKey={item.dataKey}
                          position="top"
                          style={{
                            fontSize: 13,
                            fill: item.fill,
                            fontWeight: 'bold',
                          }}
                        />
                      </Bar>
                    ))}
                  </>
                ) : showInCutawayState &&
                  useTheFactState &&
                  cardName === 'complete' ? (
                  <>
                    {completeShowInCutawayWithClosed.map((item) => (
                      <Bar
                        isAnimationActive={false}
                        key={item.dataKey}
                        dataKey={item.dataKey}
                        barSize={40}
                        radius={8}
                        fill={item.fill}
                        name={reportTitle[cardName] + ' ' + item.name}
                      >
                        <LabelList
                          dataKey={item.dataKey}
                          position="top"
                          style={{
                            fontSize: 13,
                            fill: item.fill,
                            fontWeight: 'bold',
                          }}
                        />
                      </Bar>
                    ))}
                  </>
                ) : useTheFactState && cardName === 'complete' ? (
                  <Bar
                    isAnimationActive={false}
                    dataKey="closed"
                    barSize={70}
                    radius={8}
                    fill="#27AE60"
                    name="Выполнено (Закрытые)"
                  >
                    <LabelList
                      dataKey="closed"
                      position="top"
                      style={{
                        fontSize: 13,
                        fill: '#27AE60',
                        fontWeight: 'bold',
                      }}
                    />
                  </Bar>
                ) : cardName === 'complete' &&
                  !useTheFactState &&
                  !showInCutawayState ? (
                  <Bar
                    isAnimationActive={false}
                    dataKey="fact"
                    barSize={70}
                    radius={8}
                    fill="#27AE60"
                    name="Выполнено (Факт)"
                  >
                    <LabelList
                      dataKey="fact"
                      position="top"
                      style={{
                        fontSize: 13,
                        fill: '#27AE60',
                        fontWeight: 'bold',
                      }}
                    />
                  </Bar>
                ) : cardName === 'annulled' ? (
                  <Bar
                    isAnimationActive={false}
                    dataKey="count"
                    barSize={70}
                    radius={8}
                    fill="#F2C94C"
                    name={reportTitle[cardName]}
                  >
                    <LabelList
                      dataKey="count"
                      position="top"
                      style={{
                        fontSize: 13,
                        fill: '#F2C94C',
                        fontWeight: 'bold',
                      }}
                    />
                  </Bar>
                ) : (
                  <Bar
                    isAnimationActive={false}
                    dataKey="count"
                    barSize={70}
                    radius={8}
                    fill="#2F80ED"
                    name={reportTitle[cardName]}
                  >
                    <LabelList
                      dataKey="count"
                      position="top"
                      style={{
                        fontSize: 13,
                        fill: '#2F80ED',
                        fontWeight: 'bold',
                      }}
                    />
                  </Bar>
                )}
              </>
            )}
          </BarChart>
        </ResponsiveContainer>
        {isNoData && <div className={styles.noData}>Нет данных</div>}
      </>
      <DspxPagination
        current={currentPage + 1}
        total={data.length}
        pageSize={ITEMS_PER_PAGE}
        onChange={handlePageChange}
        hideOnSinglePage
      />
    </div>
  );
};
